"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDemoMode } from "../../hooks/demoModeContext";
import { autoPublishAccounts, useAutoPublish } from "../../hooks/useAutoPublish";
import { useAutoPublishFlagState } from "../../hooks/useAutoPublishFlag";
import { useAutoPublishProjects } from "../../hooks/useAutoPublishProjects";
import { useReadyShortCount } from "../../hooks/useReadyShortCount";
import { useSocialProviderAccounts } from "../../hooks/useSocialProviderAccounts";
import type { PublicationModel } from "../../types/models";
import Button from "../Button";
import DropdownMenu from "../DropdownMenu";
import { getProjectCalendarHref } from "../calendar/projectCalendar";
import { AutoPublishSplit } from "./AutoPublishButton";
import { AutoPublishPanel, keepOpenOnCalendarClick } from "./AutoPublishControls";
import { AUTO_POST_STATUS, getAutoPublishDisplayStatus, autoPublishBusyLabel, getAutoPublishSourceFailure, autoPublishFirstPost, isAutoPostWaitingForShorts } from "./autoPostStatus";
import { AutoPublishProjectHeader, CalendarAutoPublishProjectList, CalendarAutoPublishTrigger } from "./CalendarAutoPublishMenu";
import { useAutoPublishSwitch } from "./useAutoPublishSwitch";
import { isAutoPublishProjectBlocked } from "./calendarAutoPublishStatus";

type CalendarAutoPublishActions = {
  wrapScheduleButton?: (button: ReactNode) => ReactNode;
  onManageAutoPost?: (folderId: string) => void;
};

type Props = {
  publications?: PublicationModel[];
  onOpen: () => void;
  children: (actions: CalendarAutoPublishActions) => ReactNode;
};

export default function CalendarAutoPublish(props: Props) {
  const enabled = useAutoPublishFlagState();
  // Auto post books real posts on real accounts, and the demo calendar's
  // posts and accounts aren't real: demo mode leaves it out.
  const demo = useDemoMode();
  if (demo) return props.children({});
  if (enabled === undefined) {
    return props.children({
      wrapScheduleButton: (button) => (
        <AutoPublishSplit>
          {button}
          <CalendarAutoPublishTrigger projects={[]} loading disabled className="disabled:opacity-100" />
        </AutoPublishSplit>
      ),
    });
  }
  return enabled ? <CalendarAutoPublishManager {...props} /> : props.children({});
}

/** One settings/switch owner, kept mounted while its popover closes for dialogs. */
export function CalendarAutoPublishManager({ publications, onOpen, children }: Props) {
  const projects = useAutoPublishProjects();
  const accountsQuery = useSocialProviderAccounts().readAccounts;
  const accounts = autoPublishAccounts(accountsQuery.data ?? []);
  const [open, setOpen] = useState(false);
  const [retryingProjects, setRetryingProjects] = useState(false);
  const [listFocus, setListFocus] = useState<{ edge: "first" | "last"; request: number } | null>(null);
  const [view, setView] = useState<"projects" | "settings">("projects");
  const [selection, setSelection] = useState<{ folderId: string; request: number } | null>(null);
  const [pendingToggle, setPendingToggle] = useState<{ request: number; enabled: boolean } | null>(null);
  const requestId = useRef(0);
  const handledRequest = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const entryRequest = useRef(0);
  const entryRetryInFlight = useRef(false);
  // Keep a successful empty result distinct from a library that has never loaded.
  const entryLoading = !projects.hasLoaded && (retryingProjects || projects.isFetching || !projects.error);
  const entryError = !projects.hasLoaded && Boolean(projects.error) && !entryLoading;
  const selectedProject = projects.projects.find(project => project.id === selection?.folderId);
  const settings = useAutoPublish(selection?.folderId ?? "", {
    enabled: Boolean(selection),
    // A new selection reads the current bookings before a turn-off confirmation.
    refreshKey: selection ? `calendar-${selection.request}` : undefined,
  });
  const workflow = useAutoPublishSwitch({
    surface: "calendar",
    state: settings.state,
    accounts,
    apply: settings.save,
    onDialog: () => setOpen(false),
    onReturn: () => setOpen(true),
    open: open && view === "settings",
  });
  const busy = Boolean(pendingToggle || settings.isSaving || workflow.isBusy);
  // Counted like the results header, so off reads the same: "Schedules all 9 shorts, …".
  const shortCount = useReadyShortCount(selection?.folderId);
  const displayStatus = getAutoPublishDisplayStatus({
    state: settings.statusState,
    accounts,
    taskStatus: selectedProject?.taskStatus,
    shortCount,
    isStarting: settings.isStarting,
  });
  const waitingForShorts = !settings.isLoading && !accountsQuery.isLoading && !settings.isStarting &&
    isAutoPostWaitingForShorts({
      enabled: settings.statusState?.enabled,
      taskStatus: selectedProject?.taskStatus,
      upcoming: settings.statusState?.upcoming,
      hasQueuedPosts: selectedProject?.hasQueuedPosts,
    });
  const firstPostEstimate = waitingForShorts && settings.statusState
    ? autoPublishFirstPost(settings.statusState, accounts) : null;

  useEffect(() => () => { entryRequest.current += 1; }, []);

  useEffect(() => {
    if (!retryingProjects) return;
    // There is no open popover yet to handle dismissal. Keep the read running,
    // but do not open it later if the user has moved on to another control.
    const cancelOpening = () => {
      entryRequest.current += 1;
      entryRetryInFlight.current = false;
      setRetryingProjects(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) cancelOpening();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Tab") cancelOpening();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [retryingProjects]);

  useEffect(() => {
    if (!pendingToggle || !selection || pendingToggle.request !== selection.request ||
      settings.isLoading || settings.isFetching || accountsQuery.isLoading || accountsQuery.isFetching ||
      handledRequest.current === pendingToggle.request) return;
    handledRequest.current = pendingToggle.request;
    setPendingToggle(null);
    if (settings.error || !settings.state || accountsQuery.error) {
      toast.error("Couldn't load Auto post settings. Try again.");
      return;
    }
    if (settings.state.enabled !== pendingToggle.enabled) {
      workflow.onChange({ enabled: pendingToggle.enabled });
    }
  }, [pendingToggle, selection, settings, accountsQuery, workflow]);

  useEffect(() => {
    if (!open || busy || view === "projects") return;
    const frame = requestAnimationFrame(() => {
      const target = panelRef.current?.querySelector<HTMLElement>("button:not(:disabled), a[href]");
      target?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, view, selection?.folderId, projects.isLoading, settings.isLoading, busy]);

  const selectProject = (folderId: string) => {
    if (busy) return;
    entryRequest.current += 1;
    entryRetryInFlight.current = false;
    setRetryingProjects(false);
    onOpen();
    setSelection({ folderId, request: ++requestId.current });
    setView("settings");
    setOpen(true);
  };
  const toggleProject = (folderId: string, enabled: boolean) => {
    if (busy) return;
    setListFocus(null);
    const request = ++requestId.current;
    setSelection({ folderId, request });
    setPendingToggle({ request, enabled });
  };
  const openProjects = (edge?: "first" | "last") => {
    onOpen();
    if (!busy) {
      setListFocus(edge ? { edge, request: ++requestId.current } : null);
      setView("projects");
    }
    setOpen(true);
  };
  const requestOpen = async (edge?: "first" | "last") => {
    if (projects.hasLoaded) {
      openProjects(edge);
      return;
    }
    if (entryLoading || entryRetryInFlight.current) return;
    const request = ++entryRequest.current;
    entryRetryInFlight.current = true;
    setRetryingProjects(true);
    try {
      const result = await projects.refetch({ cancelRefetch: false });
      if (request === entryRequest.current && !result.error && result.data !== undefined) openProjects(edge);
    } catch {
      // The bolt stays retryable; initial failures never create an empty popover.
    } finally {
      if (request === entryRequest.current) {
        entryRetryInFlight.current = false;
        setRetryingProjects(false);
      }
    }
  };
  const changeOpen = (next: boolean) => {
    if (next) void requestOpen();
    else {
      entryRequest.current += 1;
      entryRetryInFlight.current = false;
      setRetryingProjects(false);
      // Closing an in-flight read cancels that intent; opened dialogs own their action.
      setPendingToggle(null);
      setOpen(false);
    }
  };

  const wrapScheduleButton = (button: ReactNode) => (
    <AutoPublishSplit>
      {button}
      <DropdownMenu size="l" positions={["bottom", "top"]} align="end"
        open={open && (view === "settings" || projects.hasLoaded)} onOpenChange={changeOpen}
        onClickOutside={keepOpenOnCalendarClick}>
        <DropdownMenu.Trigger>
          <CalendarAutoPublishTrigger buttonRef={triggerRef} projects={projects.projects} publications={publications} loading={entryLoading} error={entryError}
            onKeyDown={(event) => {
              if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
              event.preventDefault();
              event.stopPropagation();
              void requestOpen(event.key === "ArrowUp" ? "last" : "first");
            }} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content disablePadding className={`${view === "settings" ? "w-80" : "w-96"} max-w-[calc(100vw-1rem)]`}>
          <div ref={panelRef} onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              changeOpen(false);
              triggerRef.current?.focus({ preventScroll: true });
            }
          }}>
            {view === "projects" ? (
              <CalendarAutoPublishProjectList
                projects={projects.projects}
                accounts={accounts}
                selectedProjectId={selection?.folderId}
                focusEdge={listFocus}
                publications={publications}
                onSelect={selectProject}
                onToggle={toggleProject}
                busyProjectId={busy ? selection?.folderId : null}
                busyStatus={settings.isSaving ? displayStatus : undefined}
                isStarting={settings.isStarting || workflow.checking || pendingToggle?.enabled === true}
                disabled={busy}
              />
            ) : (
              <>
                <AutoPublishProjectHeader project={selectedProject} onBack={() => { setListFocus(null); setView("projects"); }} />
                <div className="max-h-[calc(100dvh-10rem)] overflow-y-auto">
                  {settings.isLoading || accountsQuery.isLoading ? (
                    <AutoPublishPanel state={null} accounts={[]} busyLabel={AUTO_POST_STATUS.loading} disabled
                      onChange={() => undefined} onConnect={() => undefined} />
                  ) : settings.error || !settings.state || accountsQuery.error ? (
                    <div role="alert" className="p-4 text-sm text-slate-500">
                      <p>Auto post settings aren’t available. The project may have been removed.</p>
                      <Button size="s" variant="secondary" className="mt-3" onClick={() => {
                        void settings.refetch();
                        void projects.refetch();
                        void accountsQuery.refetch();
                      }}>Try again</Button>
                    </div>
                  ) : (
                    <fieldset disabled={settings.isSaving || workflow.isBusy} className="min-w-0">
                      <AutoPublishPanel
                        state={workflow.shownState}
                        accounts={accounts}
                        rawAccounts={accountsQuery.data}
                        status={displayStatus}
                        waitingForShorts={waitingForShorts}
                        firstPostEstimate={firstPostEstimate}
                        sourceFailure={getAutoPublishSourceFailure(selectedProject?.taskStatus)}
                        sourceBlocked={selectedProject ? isAutoPublishProjectBlocked({
                          ...selectedProject,
                          hasReadyShorts: selectedProject.hasReadyShorts || (shortCount ?? 0) > 0,
                          hasQueuedPosts: selectedProject.hasQueuedPosts || (settings.statusState?.upcoming ?? 0) > 0,
                        }) : false}
                        shortCount={shortCount}
                        queue={!settings.isStarting && settings.statusState?.enabled ? settings.statusState : undefined}
                        calendarHref={selection ? getProjectCalendarHref(selection.folderId, settings.statusState?.nextPostAt) : undefined}
                        onShowInCalendar={() => changeOpen(false)}
                        disabled={settings.isSaving || workflow.isBusy}
                        checking={workflow.checking}
                        busyLabel={autoPublishBusyLabel(settings)}
                        onSwitchIntent={workflow.prefetch}
                        onChange={change => { if (!settings.isSaving && !workflow.isBusy) workflow.onChange(change); }}
                        onConnect={() => { if (!settings.isSaving && !workflow.isBusy) workflow.openConnect(); }}
                        onReconnect={account => { if (!settings.isSaving && !workflow.isBusy) workflow.reconnectAccount(account); }}
                      />
                    </fieldset>
                  )}
                </div>
              </>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu>
    </AutoPublishSplit>
  );

  return (
    <>
      {children({ wrapScheduleButton, onManageAutoPost: selectProject })}
      {workflow.layer}
    </>
  );
}
